import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  diagnosisService,
  CreateDiagnosisInput,
  UpdateDiagnosisInput,
  DiagnosisSearchParams,
} from "../../services/admin/diagnosis.service";
import { stainsService } from "../../services/admin/stains.service";

export function useDiagnosisAdmin() {
  return useQuery({
    queryKey: ["diagnosis", "admin"],
    queryFn: () => diagnosisService.getAll(),
  });
}

export function useDiagnosisSearch(params: DiagnosisSearchParams) {
  return useQuery({
    queryKey: ["diagnosis", "search", params],
    queryFn: () => diagnosisService.search(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateDiagnosis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDiagnosisInput) => diagnosisService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-macros"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis macro created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create diagnosis macro",
        color: "red",
      });
    },
  });
}

export function useUpdateDiagnosis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateDiagnosisInput) => diagnosisService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-macros"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis macro updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update diagnosis macro",
        color: "red",
      });
    },
  });
}

export function useToggleDiagnosisActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      diagnosisService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

export function useDeleteDiagnosis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => diagnosisService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis macro deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete diagnosis macro",
        color: "red",
      });
    },
  });
}

export function useDiagnosisLinkedStains(diagnosisId: string | undefined) {
  return useQuery({
    queryKey: ["diagnosis", diagnosisId, "linked-stains"],
    queryFn: () => diagnosisService.getLinkedStains(diagnosisId!),
    enabled: !!diagnosisId,
  });
}

export function useAllActiveStains() {
  return useQuery({
    queryKey: ["admin-stains", "active"],
    queryFn: () => stainsService.getAll({ status: "active" }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetDiagnosisLinkedStains() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      diagnosisId,
      stainIds,
    }: {
      diagnosisId: string;
      stainIds: string[];
    }) => diagnosisService.setLinkedStains(diagnosisId, stainIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["diagnosis", variables.diagnosisId, "linked-stains"],
      });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-macros"] });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update linked stains",
        color: "red",
      });
    },
  });
}
